import React from 'react';

import { useMenuContext } from '../../ContextState/useMenuContext';
import { BackNavigation } from '../Components/BackNavigation';
import { MobileErrorView } from '../Components/MobileErrorView';
import { MobileLoadingMenu } from '../Components/MobileLoadingMenu';
import { MobileMenuItem } from '../Components/MobileMenuItem';
import { SubmenuHeader } from '../Components/SubmenuHeader';

import { DATA_OG, DATA_TEST, getDynamicDataOGId, getDynamicDataTestId } from '../../enums';
import { IMenuItem } from '../../types';
import { MobileKeyboardNavContextProps, useMobileKeyboardNavContext } from '../Context/MobileKeyboardNavContext';

interface IMobileLevelTwoMenuProps {
  closeMenu(): void;
}

export const MobileLevelTwoMenu: React.FC<IMobileLevelTwoMenuProps> = ({ closeMenu }) => {
  const {
    currentLevelOneItem,
    submenuLoadingItemId,
    setSubmenuLoadingItemId,
    setSelectedLevelTwoId,
    setCurrentLevelOneItem,
    fetchLevelTwoSubmenuSections,
    setLevelTwoLoadingError,
    levelTwoLoadingError
  } = useMenuContext();
  const { mobileLevelTwoMenuItemIdToFocusNext, setMobileLevelTwoMenuItemIdToFocusNext } =
    useMobileKeyboardNavContext() as MobileKeyboardNavContextProps;
  const areSubMenuItemsEmpty =
    !currentLevelOneItem?.submenuSections || currentLevelOneItem?.submenuSections.length === 0;

  React.useEffect(() => {
    if (currentLevelOneItem?.shouldFetchNestedMenuItems && areSubMenuItemsEmpty) {
      setSubmenuLoadingItemId(currentLevelOneItem.id);
      const fetchSubmenuSections = async () => {
        await fetchLevelTwoSubmenuSections(currentLevelOneItem)
          .catch(() => {
            setLevelTwoLoadingError(true);
          })
          .finally(() => {
            setSubmenuLoadingItemId(undefined);
          });
      };
      void fetchSubmenuSections();
    }
  }, [currentLevelOneItem]);

  const renderLoadingMenu = () => {
    if (submenuLoadingItemId && !levelTwoLoadingError) {
      return <MobileLoadingMenu dataTest={DATA_TEST.mobile_level_two_loading_menu} />;
    }
  };

  const renderErrorView = () => {
    if (levelTwoLoadingError) {
      return <MobileErrorView dataTest={DATA_TEST.mobile_level_two_error_view} />;
    }
  };

  const handleMenuItemClick = (item: IMenuItem) => {
    if (item.onClick) {
      item.onClick();
      closeMenu();
    } else if (item.url) {
      closeMenu();
    } else {
      setSelectedLevelTwoId(item.id);
      setMobileLevelTwoMenuItemIdToFocusNext(item.id);
    }
  };

  const renderSubmenuSections = () => {
    return currentLevelOneItem?.submenuSections?.map((submenuSection) => {
      const { title, items: submenuItems } = submenuSection;

      const titleElement = title ? <SubmenuHeader headerText={title} key={title} /> : null;

      const submenuItemElements = submenuItems?.map((item) => {
        const dataTest = item.url
          ? getDynamicDataTestId(DATA_TEST.mobile_level_two_link, item.id)
          : getDynamicDataTestId(DATA_TEST.mobile_level_two_item, item.id);

        return (
          <MobileMenuItem
            dataTest={dataTest}
            dataOG={getDynamicDataOGId(DATA_OG.menu_item, item.label)}
            item={item}
            key={item.id}
            autoFocus={mobileLevelTwoMenuItemIdToFocusNext === item.id}
            handleClick={handleMenuItemClick}
          />
        );
      });

      return [titleElement, submenuItemElements];
    });
  };

  return (
    <>
      <BackNavigation
        onClick={() => {
          setCurrentLevelOneItem(null);
        }}
        title={currentLevelOneItem?.label}
      />
      {renderLoadingMenu()}
      {renderErrorView()}
      {!submenuLoadingItemId &&
        !levelTwoLoadingError &&
        currentLevelOneItem?.submenuSections &&
        renderSubmenuSections()}
    </>
  );
};
